import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';

// =============================================================================
// TIPOS
// =============================================================================

export type TarjetaRed = {
  id_tarjeta_red?: string;
  id_tipo_red: string;
  nombre: string; // viene de tipo_red
  icono?: string; // viene de tipo_red
  url_base?: string; // viene de tipo_red
  url: string; // valor propio de la tarjeta
};

export type BusinessCard = {
  id_tarjeta?: string;
  id_empresa: string;
  nombre: string;
  puesto?: string;
  email?: string;
  telefono?: string;
  direccion?: string;
  descripcion?: string;
  mostrar_qr_citas: boolean;
  mostrar_qr_catalogo: boolean;
  paleta: string;
  nombre_empresa?: string;
  logo_empresa?: string;
  qr_citas_url?: string;
  qr_catalogo_url?: string;
  redes?: TarjetaRed[];
};

type OmitReadonly =
  | 'id_tarjeta'
  | 'nombre_empresa'
  | 'logo_empresa'
  | 'qr_citas_url'
  | 'qr_catalogo_url';

// Lo que se persiste en tarjeta_redes (sin campos derivados de tipo_red)
type TarjetaRedInsert = {
  id_tipo_red: string;
  url: string;
};

// =============================================================================
// HOOK
// =============================================================================

export const useBusinessCards = (id_empresa: string | undefined) => {
  const [cards, setCards] = useState<BusinessCard[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // FETCH
  // ---------------------------------------------------------------------------

  const fetchCards = useCallback(async () => {
    if (!id_empresa) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('vista_tarjetas_presentacion')
        .select('*')
        .eq('id_empresa', id_empresa);

      if (err) throw err;
      setCards((data as BusinessCard[]) ?? []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar tarjetas');
    } finally {
      setLoading(false);
    }
  }, [id_empresa]);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  // ---------------------------------------------------------------------------
  // INSERT
  // ---------------------------------------------------------------------------

  const insertCard = async (card: Omit<BusinessCard, OmitReadonly>) => {
    if (!id_empresa) return { ok: false };
    setSaving(true);
    setError(null);
    try {
      const { redes, ...cardData } = card;

      const { data, error: err } = await supabase
        .from('tarjetas_presentacion')
        .insert({ ...cardData, id_empresa })
        .select()
        .single();

      if (err) throw err;

      if (redes?.length) {
        const redesInsert: (TarjetaRedInsert & { id_tarjeta: string })[] =
          redes.map(({ id_tipo_red, url }) => ({
            id_tipo_red,
            url,
            id_tarjeta: data.id_tarjeta,
          }));

        const { error: redesErr } = await supabase
          .from('tarjeta_redes')
          .insert(redesInsert);

        if (redesErr) throw redesErr;
      }

      await fetchCards();
      return { ok: true };
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al crear tarjeta');
      return { ok: false };
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // UPDATE
  // ---------------------------------------------------------------------------

  const updateCard = async (
    id_tarjeta: string,
    card: Partial<Omit<BusinessCard, OmitReadonly>>
  ) => {
    setSaving(true);
    setError(null);
    try {
      const { redes, ...cardData } = card;

      const { error: err } = await supabase
        .from('tarjetas_presentacion')
        .update({ ...cardData, fecha_edicion: new Date().toISOString() })
        .eq('id_tarjeta', id_tarjeta);

      if (err) throw err;

      if (redes !== undefined) {
        const { error: deleteErr } = await supabase
          .from('tarjeta_redes')
          .delete()
          .eq('id_tarjeta', id_tarjeta);

        if (deleteErr) throw deleteErr;

        if (redes.length) {
          const redesInsert: (TarjetaRedInsert & { id_tarjeta: string })[] =
            redes.map(({ id_tipo_red, url }) => ({
              id_tipo_red,
              url,
              id_tarjeta,
            }));

          const { error: redesErr } = await supabase
            .from('tarjeta_redes')
            .insert(redesInsert);

          if (redesErr) throw redesErr;
        }
      }

      await fetchCards();
      return { ok: true };
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al actualizar tarjeta');
      return { ok: false };
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // DELETE (soft)
  // ---------------------------------------------------------------------------

  const deleteCard = async (id_tarjeta: string) => {
    setSaving(true);
    setError(null);
    try {
      const { error: err } = await supabase
        .from('tarjetas_presentacion')
        .update({ activo: false, fecha_edicion: new Date().toISOString() })
        .eq('id_tarjeta', id_tarjeta);

      if (err) throw err;

      setCards((prev) => prev.filter((c) => c.id_tarjeta !== id_tarjeta));
      return { ok: true };
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al eliminar tarjeta');
      return { ok: false };
    } finally {
      setSaving(false);
    }
  };

  return {
    cards,
    loading,
    saving,
    error,
    fetchCards,
    insertCard,
    updateCard,
    deleteCard,
  };
};
